import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { isAxiosError } from 'axios';
import {
  apiService,
  Project,
  ProjectSummary,
  Schema,
  resolveProjectSummaryTargetSchema,
} from '../services/api';
import { getApiErrorMessage } from '../services/apiErrors';
import { useAuth } from './AuthContext';
import { SELECTED_PROJECT_KEY } from '../utils/storageKeys';
import { isDeletedResourceError } from '../utils/deletedResource';

interface ProjectContextValue {
  projects: Project[];
  selectedProjectId: string | null;
  setSelectedProjectId: (projectId: string) => void;
  projectSummary?: ProjectSummary;
  targetSchema?: Schema | null;
  isLoadingProjects: boolean;
  isLoadingSummary: boolean;
  refreshSummary: () => void;
  createProject: (payload: CreateProjectInput) => Promise<Project>;
  isCreatingProject: boolean;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

const DEFAULT_PROJECT_ID = process.env.REACT_APP_DEFAULT_PROJECT_ID || '';

function isMissingProjectError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

function loadPersistedProjectId(): string | null {
  try {
    const stored = localStorage.getItem(SELECTED_PROJECT_KEY);
    if (stored && stored.trim()) return stored.trim();
  } catch {
    // localStorage may be unavailable
  }
  return DEFAULT_PROJECT_ID || null;
}

function persistProjectId(id: string | null) {
  try {
    if (id) {
      localStorage.setItem(SELECTED_PROJECT_KEY, id);
    } else {
      localStorage.removeItem(SELECTED_PROJECT_KEY);
    }
  } catch {
    // ignore
  }
}

export interface CreateProjectInput {
  tenantId?: string;
  name: string;
  description?: string;
  status?: string;
  targetSchemaId?: string | null;
  metadata?: Record<string, any>;
  createdBy?: string | null;
}

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [lastSuccessfulProjects, setLastSuccessfulProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectIdRaw] = useState<string | null>(
    loadPersistedProjectId
  );

  // Wrap setter to also persist
  const setSelectedProjectId = useCallback((id: string | null) => {
    setSelectedProjectIdRaw(id);
    persistProjectId(id);
  }, []);

  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    isError: isProjectsError,
  } = useQuery<Project[]>(
    ['projects'],
    () => apiService.listProjects({}),
    {
      enabled: isAuthenticated,
      staleTime: 5 * 60 * 1000,
      onSuccess: (projects) => {
        setLastSuccessfulProjects(projects);
      },
    }
  );
  const projects = projectsData ?? (isProjectsError ? lastSuccessfulProjects : []);

  // Ensure we always have a valid selected project
  useEffect(() => {
    if (projects.length === 0) return;

    // If current selection exists in the project list, keep it
    if (selectedProjectId && projects.some((p) => p.project_id === selectedProjectId)) {
      return;
    }

    // Persisted/selected project no longer valid — pick a new one
    if (DEFAULT_PROJECT_ID && projects.some((p) => p.project_id === DEFAULT_PROJECT_ID)) {
      setSelectedProjectId(DEFAULT_PROJECT_ID);
      return;
    }
    setSelectedProjectId(projects[0].project_id);
  }, [projects, selectedProjectId, setSelectedProjectId]);

  const {
    data: projectSummary,
    isLoading: isLoadingSummary,
    error: projectSummaryError,
    isError: isProjectSummaryError,
  } = useQuery<ProjectSummary>(
    ['project-summary', selectedProjectId],
    () => apiService.getProjectSummary(selectedProjectId!),
    {
      enabled: Boolean(selectedProjectId) && isAuthenticated,
      staleTime: 60 * 1000,
      retry: (failureCount, error) =>
        !isDeletedResourceError(error) && !isMissingProjectError(error) && failureCount < 2,
    }
  );

  const projectUnavailable =
    Boolean(selectedProjectId) && isProjectSummaryError && isDeletedResourceError(projectSummaryError);
  const projectMissing =
    Boolean(selectedProjectId) && isProjectSummaryError && isMissingProjectError(projectSummaryError);

  useEffect(() => {
    if (!projectUnavailable && !projectMissing) return;
    persistProjectId(null);
    setSelectedProjectIdRaw(null);
    void queryClient.invalidateQueries(['projects']);
  }, [projectMissing, projectUnavailable, queryClient]);

  /** Invalidate + refetch project summary (e.g. Transformations tab). Bypasses staleTime. */
  const refreshSummary = useCallback(() => {
    if (!selectedProjectId) return;
    void queryClient.invalidateQueries(['project-summary', selectedProjectId]);
  }, [queryClient, selectedProjectId]);

  const createProjectMutation = useMutation(
    (input: CreateProjectInput) =>
      apiService.createProject({
        tenantId: input.tenantId,
        name: input.name,
        description: input.description,
        status: input.status ?? 'active',
        targetSchemaId: input.targetSchemaId ?? null,
        metadata: input.metadata ?? {},
        createdBy: input.createdBy ?? 'web-ui',
      }),
    {
      onSuccess: (project) => {
        queryClient.setQueryData<Project[]>(['projects'], (existing = []) => {
          const filtered = existing.filter((item) => item.project_id !== project.project_id);
          const updated = [...filtered, project].sort((a, b) => a.name.localeCompare(b.name));
          return updated;
        });
        setSelectedProjectId(project.project_id);
        queryClient.invalidateQueries(['project-summary', project.project_id]);
      },
      onError: (error) => {
        // Keep react-query error consistent with backend detail (409 duplicate name, etc.)
        throw new Error(getApiErrorMessage(error));
      },
    }
  );

  const createProjectHandler = useCallback(
    async (input: CreateProjectInput) => {
      const project = await createProjectMutation.mutateAsync(input);
      await queryClient.invalidateQueries(['projects']);
      return project;
    },
    [createProjectMutation, queryClient]
  );

  const value = useMemo<ProjectContextValue>(
    () => ({
      projects,
      selectedProjectId,
      setSelectedProjectId: (projectId: string) => {
        setSelectedProjectId(projectId);
        queryClient.invalidateQueries(['project-summary', projectId]);
      },
      projectSummary,
      targetSchema: resolveProjectSummaryTargetSchema(projectSummary),
      isLoadingProjects,
      isLoadingSummary,
      refreshSummary,
      createProject: createProjectHandler,
      isCreatingProject: createProjectMutation.isLoading,
    }),
    [
      projects,
      selectedProjectId,
      projectSummary,
      isLoadingProjects,
      isLoadingSummary,
      refreshSummary,
      createProjectHandler,
      createProjectMutation.isLoading,
    ]
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export const useProjectContext = (): ProjectContextValue => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjectContext must be used within a ProjectProvider');
  }
  return context;
};


